/* eslint-disable */

import PropTypes from 'prop-types';
import React, { useMemo, useReducer } from 'react';

import {
  SET_CURRENT_CALC_SECTION,
  SET_GLOBAL_INPUT_TYPES,
  SET_CALCULATORS_LIST,
  SET_SCORE,
  SET_LAST_VISITED_INDEX,
  SET_CALCULATOR_NAME,
  SET_CHART_DATA,
  SET_CONFIG,
  SET_ANSWERS_DATA,
  SET_QUESTIONS_MAP,
  SET_CALC_DATA,
  SET_SHARED_ANSWERS_DATA,
  SET_DIRTY_ANSWER_MAP,
  ADD_CUSTOM_CO2_ITEMS,
  ADD_CUSTOM_CO2_SUPPLIERS,
  SET_CUSTOM_CO2_ANSWERS,
  SET_CUSTOM_CO2_ACTIVITY,
} from '../types';
import { CALC_MODELS, DefaultCalculatorPrices } from '../../constants';

import CalculatorsContext from './calculatorsContext';
import calculatorsReducer from './calculatorsReducer';

const CalculatorsState = ({ children }) => {
  const initialDirtyAnswersMap = Object.keys(CALC_MODELS).reduce(
    (acc, model) => ({ ...acc, [model]: false }),
    {}
  );

  const initialState = {
    questions: [],
    calcSteps: [],
    carbonStructure: {
      activities: [],
      types: [],
      units: [],
      classes: [],
    },
    calculatorsList: [],
    carbonSources: {
      product: [],
      package: [],
    },
    company: {},
    fullChartData: [],
    chartData: [],
    lastVisitedIndex: -1,
    // calculatorAnswers: { ...DefaultCalculatorAnswers },
    calculatorAnswers: {},
    companyAnswers: {},
    score: {
      environmental: 0,
      social: 0,
      governance: 0,
      total: 0,
      productTotal: 0,
      packageTotal: 0,
      companyTotal: 0,
      testYear: new Date().getFullYear(),
    },
    calculatorPrices: { ...DefaultCalculatorPrices },
    calculatorCost: 0,
    calculatorName: '',
    chartCreated: false,
    config: null,
    shares: null,
    dirtyAnswersMap: initialDirtyAnswersMap,
  };

  // const [filesList, setFilesList] = useState([]);
  const [state, dispatch] = useReducer(calculatorsReducer, initialState);

  //----------------------------------------------
  const buildCalcSteps = (carbonData, customCo2NeededScopes) => {
    // carbonData – это то, что приходит из бэка (scopes: [ {categories: [...]}, ... ] ),
    // обьект с вопросами
    // customCo2NeededScopes – массив индексов scope, перед которыми надо вставлять customCo2

    let steps = [];
    let runningIndex = 0;

    carbonData.forEach((scope, sIndex) => {
      // Если мы хотим вставить customCo2 до данного scope:
      if (customCo2NeededScopes.includes(sIndex)) {
        steps.push({
          index: runningIndex++,
          label: `Custom CO2 for scope${sIndex}`,
          model: CALC_MODELS.CUSTOM_CO2,
          scopeIndx: sIndex,
          route: `/calculators/carbon/${runningIndex}`,
          id: `${CALC_MODELS.CUSTOM_CO2}_${sIndex}`,
        });
      }

      scope.categories.forEach((cat, cIndex) => {
        cat.questions.forEach((q, qIndex) => {
          steps.push({
            index: runningIndex++,
            label: `${scope.name} / ${cat.name} / ${q.name}`,
            model: 'carbon',
            scopeIndx: sIndex,
            categoryIndx: cIndex,
            questionIndx: qIndex,
            route: `/calculators/carbon/${runningIndex}`,
            id: q.id,
          });
        });
      });
    });
    return steps;
  };

  const isAnyAnswerExists = () => {
    const { answers } = state;
    let result = false;

    if (answers?.carbon) {
      Object.keys(answers?.carbon).map((cat) => {
        const category = answers?.carbon[cat];
        Object.keys(category).forEach((key) => {
          const activities = category[key];
          activities.forEach((el) => {
            if (el?.filled) {
              result = true;
            }
          });
        });
      });
    }
    if (answers?.[CALC_MODELS.CUSTOM_CO2]?.answers) {
      Object.keys(answers?.[CALC_MODELS.CUSTOM_CO2]?.answers).map((key) => {
        const activities = answers?.[CALC_MODELS.CUSTOM_CO2]?.answers[key];
        activities.forEach((el) => {
          if (el?.filled) {
            result = true;
          }
        });
      });
    }
    return result;
  };

  const setChartData = (data) => {
    dispatch({
      type: SET_CHART_DATA,
      payload: { chartData: [...data] },
    });
  };

  const setConfig = (data) => {
    const calcs = data.models.map((m) => m.model);
    dispatch({
      type: SET_CONFIG,
      payload: { ...data },
    });
    dispatch({
      type: SET_CALCULATORS_LIST,
      payload: [...calcs],
    });
    dispatch({
      type: SET_CALCULATOR_NAME,
      payload: data.code,
    });
  };

  const setCurrentCalcSection = (data) => {
    dispatch({
      type: SET_CURRENT_CALC_SECTION,
      payload: data,
    });
  };

  const changeGlobalInputTypesData = (data) => {
    const { GlobalVariants } = data;
    dispatch({
      type: SET_GLOBAL_INPUT_TYPES,
      payload: {
        globalInputTypes: JSON.parse(GlobalVariants),
      },
    });
  };

  const setQuestionsData = (questions) => {
    const calcSteps = buildCalcSteps(questions.carbon, [0, 1]);
    dispatch({
      type: SET_CALC_DATA,
      payload: {
        calcSteps,
        questions,
      },
    });
  };

  const setQuestionsMap = (map) => {
    dispatch({
      type: SET_QUESTIONS_MAP,
      payload: map,
    });
  };

  const setAnswersData = (answers, loading = false) => {
    const dirtyMap = Object.keys(answers).reduce((acc, model) => ({ ...acc, [model]: true }), {});
    dispatch({
      type: SET_ANSWERS_DATA,
      payload: answers,
    });
  };

  const setDirtyAnswersMap = (value) => {
    dispatch({
      type: SET_DIRTY_ANSWER_MAP,
      payload: { ...value },
    });
  };

  const setSharedQuestionAnswers = (answers) => {
    dispatch({
      type: SET_SHARED_ANSWERS_DATA,
      payload: answers,
    });
  };

  const setScore = (score) => {
    dispatch({
      type: SET_SCORE,
      payload: score,
    });
  };

  const changeLastVisitedIndex = (index) => {
    dispatch({
      type: SET_LAST_VISITED_INDEX,
      payload: index,
    });
  };

  /*
  export const ADD_CUSTOM_CO2_ITEMS = 'ADD_CUSTOM_CO2_ITEMS';
  export const ADD_CUSTOM_CO2_SUPPLIERS = 'ADD_CUSTOM_CO2_SUPPLIERS';
  export const SET_CUSTOM_CO2_ANSWERS = 'SET_CUSTOM_CO2_ANSWERS';
  export const SET_CUSTOM_CO2_ACTIVITY = 'SET_CUSTOM_CO2_ACTIVITY';
  */
  const addCustomCo2Items = (item) => {
    dispatch({
      type: ADD_CUSTOM_CO2_ITEMS,
      payload: item,
    });
  };

  const addCustomCo2Suppliers = (supplier) => {
    dispatch({
      type: ADD_CUSTOM_CO2_SUPPLIERS,
      payload: supplier,
    });
  };

  const setCustomCo2Answers = (answers) => {
    dispatch({
      type: SET_CUSTOM_CO2_ANSWERS,
      payload: answers,
    });
  };

  const setCustomCo2Activity = (scopeIndex, activities) => {
    dispatch({
      type: SET_CUSTOM_CO2_ACTIVITY,
      payload: { scopeIndex, activities },
    });
  };

  const value = useMemo(
    () => ({
      state,
      questions: state.questions,
      answers: state.answers,
      calcSteps: state.calcSteps,
      localQuestions: state.questions?.local,
      carbonQuestions: state.questions?.carbon,
      globalQuestions: state.questions?.global,
      localAnswers: state.answers?.local,
      carbonAnswers: state.answers?.carbon,
      globalAnswers: state.answers?.global,
      customCo2Answers: state.answers?.[CALC_MODELS.CUSTOM_CO2],
      globalInputTypes: state.globalInputTypes,
      calculatorsList: state.calculatorsList,
      questionsMap: state.questionsMap,
      fullChartData: state.fullChartData,
      score: state.score,
      lastVisitedIndex: state.lastVisitedIndex,
      calculatorName: state.calculatorName,
      chartData: state.chartData,
      calculatorCost: state.calculatorCost,
      calculatorPrices: state.calculatorPrices,
      chartCreated: state.chartCreated,
      section: state.section,
      config: state.config,
      sharedQuestionAnswers: state.shares,
      dirtyAnswersMap: state.dirtyAnswersMap,
      co2Items: state.answers?.[CALC_MODELS.CUSTOM_CO2]?.items || [],
      co2Suppliers: state.answers?.[CALC_MODELS.CUSTOM_CO2]?.suppliers || [],
      changeGlobalInputTypesData,
      setQuestionsData,
      setAnswersData,
      setQuestionsMap,
      isAnyAnswerExists,
      setScore,
      changeLastVisitedIndex,
      setCurrentCalcSection,
      setConfig,
      setChartData,
      setSharedQuestionAnswers,
      setDirtyAnswersMap,
      addCustomCo2Items,
      addCustomCo2Suppliers,
      setCustomCo2Answers,
      setCustomCo2Activity,
    }),
    [
      state,
      changeGlobalInputTypesData,
      setQuestionsData,
      setAnswersData,
      setQuestionsMap,
      isAnyAnswerExists,
      setScore,
      changeLastVisitedIndex,
      setCurrentCalcSection,
      setConfig,
      setChartData,
      setSharedQuestionAnswers,
      setDirtyAnswersMap,
      addCustomCo2Items,
      addCustomCo2Suppliers,
      setCustomCo2Answers,
      setCustomCo2Activity,
    ]
  );
  return <CalculatorsContext.Provider value={value}>{children}</CalculatorsContext.Provider>;
};

CalculatorsState.propTypes = {
  children: PropTypes.node.isRequired,
};

export default CalculatorsState;
